import { useGetDriverFaceCluster } from '@cobliteam/hooks/src/useCamera/useDriverFaceCluster';
import { useTranslation } from '@cobliteam/i18n';
import FileUpload, { type FileMetadata, FileUploadPreview } from '@cobliteam/ui/components/FileUpload';
import Popover from '@cobliteam/ui/components/Popover';
import Skeleton from '@cobliteam/ui/components/Skeleton';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { DRIVER_FACE_PHOTO_MAX } from '../constants';
import { DriverFaceIdEmptyBanner } from './DriverFaceIdEmptyBanner/DriverFaceIdEmptyBanner';

interface DriverPhotosUploadProps {
  driverId: string;
  onAddMoreClusters: () => void;
  disabled?: boolean;
}

const DriverPhotosUpload = ({ driverId, onAddMoreClusters, disabled }: DriverPhotosUploadProps) => {
  const { t } = useTranslation('drivers');
  const { data: driverFaceCluster, isLoading } = useGetDriverFaceCluster(driverId, { enabled: !!driverId });

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <div className='flex items-center gap-2'>
          <Skeleton height={20} width={20} className='rounded' />
          <Skeleton height={20} width={200} />
          <Skeleton height={16} width={16} className='rounded-full' />
        </div>

        <div className='flex items-center justify-between gap-2 rounded-lg border border-gray-3 p-4'>
          <div className='flex items-center gap-2'>
            <Skeleton height={36} width={36} className='rounded-full' />
            <Skeleton height={20} width={240} />
          </div>
          <Skeleton height={16} width={16} className='rounded-full' />
        </div>

        <div className='mt-2 flex flex-wrap gap-4'>
          {['skeleton-1', 'skeleton-2', 'skeleton-3', 'skeleton-4', 'skeleton-5', 'skeleton-6'].map((skeletonId) => (
            <Skeleton key={skeletonId} className='size-28 rounded-lg' />
          ))}
        </div>

        <div className='overflow-hidden rounded-lg border border-gray-3'>
          <Skeleton className='aspect-[744/108] w-full rounded-none' />
          <div className='space-y-4 p-4'>
            <Skeleton height={20} width='70%' />
            <div className='flex items-center gap-4'>
              <Skeleton height={36} width={160} className='rounded-md' />
              <Skeleton height={16} width={180} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sampleUrls = driverFaceCluster?.sampleImageUrls ?? [];
  const photos: FileMetadata[] = sampleUrls.flatMap((sample) => {
    if (!sample) return [];
    const { imageUrl } = sample;
    return [
      {
        url: imageUrl,
        id: imageUrl,
        name: imageUrl,
        size: 0,
        type: '',
        faceImageUrl: imageUrl,
      },
    ];
  });

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-2'>
        <Icon name='video' size='md' />
        <Typography variant='text-md-semibold'>{t('face_id.drawer.review.photos_count', { count: photos.length, max: DRIVER_FACE_PHOTO_MAX })}</Typography>
        <Popover>
          <Popover.Trigger>
            <Popover.Icon />
          </Popover.Trigger>
          <Popover.Content className='w-60 space-y-2'>
            <Typography variant='text-md-semibold'>{t('face_id.drawer.review.photos_popover_title')}</Typography>
            <Typography variant='text-md' color='secondary'>
              {t('face_id.drawer.review.photos_popover_description')}
            </Typography>
          </Popover.Content>
        </Popover>
      </div>

      <div className='flex items-center justify-between gap-2 rounded-lg border border-gray-3 p-4'>
        <div className='flex items-center gap-2'>
          <div className='flex items-center justify-center rounded-full bg-gray-3 p-2'>
            <Icon name='camera' color='primary' />
          </div>
          <Typography variant='text-md-semibold'>{t('face_id.drawer.review.camera_only_banner_title')}</Typography>
        </div>
        <Popover>
          <Popover.Trigger>
            <Popover.Icon />
          </Popover.Trigger>
          <Popover.Content className='w-60 space-y-2'>
            <Typography variant='text-md-semibold'>{t('face_id.drawer.review.camera_only_banner_popover_title')}</Typography>
            <Typography variant='text-md' color='secondary'>
              {t('face_id.drawer.review.camera_only_banner_popover_description')}
            </Typography>
            <Typography variant='text-md' color='secondary'>
              {t('face_id.drawer.review.legacy_photos_notice')}
            </Typography>
          </Popover.Content>
        </Popover>
      </div>

      <FileUpload files={photos}>
        <FileUploadPreview />
      </FileUpload>

      <DriverFaceIdEmptyBanner
        onLinkPhotosClick={onAddMoreClusters}
        disabled={disabled}
        title={t('face_id.drawer.review.add_more_title')}
        subtitle=''
        helper={t('face_id.drawer.review.add_more_description')}
      />
    </div>
  );
};

export default DriverPhotosUpload;
